"use client"

import { useState } from "react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"



interface ExcludeContactDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  contactName: string | null
  onConfirm: () => Promise<void>
  /** `include` is the inverse action, offered when the contact is already excluded. */
  mode?: "exclude" | "include"
}

/**
 * Confirmation for the inbox exclusion actions, both directions. Owns its
 * submission flow; the parent owns open/close state and the refresh callback.
 */
export function ExcludeContactDialog({
  open,
  onOpenChange,
  contactName,
  onConfirm,
  mode = "exclude",
}: ExcludeContactDialogProps) {
  const tUi = useTranslations("Messaging.AgentsMessagingInboxExcludeContactDialog")
  const tCommonUi = useTranslations("Common")

  const [isSubmitting, setIsSubmitting] = useState(false)
  const contact = contactName ?? "this contact"
  const isInclude = mode === "include"

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <span key={isInclude ? tUi("letAIReplyToThisContactAgain") : tUi("excludeContactFromAI")}>{isInclude ? tUi("letAIReplyToThisContactAgain") : tUi("excludeContactFromAI")}</span>
          </AlertDialogTitle>
          <AlertDialogDescription>
            {isInclude ? (
              <><TranslationText>{tUi.rich("value0IsRemovedFromTheAgentSAI", { value0: String(contact) })}</TranslationText></>
            ) : (
              <><TranslationText>{tUi.rich("aIWillStopReplyingToValue0ImmediatelyIn", { value0: String(contact) })}</TranslationText></>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isSubmitting}><span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></AlertDialogCancel>
          <AlertDialogAction
            disabled={isSubmitting}
            onClick={async (event) => {
              // Radix closes synchronously on click — keep it open for the async call
              event.preventDefault()
              setIsSubmitting(true)
              try {
                await onConfirm()
                onOpenChange(false)
              } catch {
                // Error toast comes from the caller's mutation; stay open for retry.
              } finally {
                setIsSubmitting(false)
              }
            }}
          >
            <span key={isInclude
              ? isSubmitting
                ? tUi("removing")
                : tUi("removeFromList")
              : isSubmitting
                ? tUi("excluding")
                : tUi("excludeContact")}>{isInclude
              ? isSubmitting
                ? tUi("removing")
                : tUi("removeFromList")
              : isSubmitting
                ? tUi("excluding")
                : tUi("excludeContact")}</span>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
